import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { TESTIDS } from "@/constants/testIds";
import { Loader2, Phone, Eye, EyeOff } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function LoginPage() {
  const [tab, setTab] = useState("mobile"); // "mobile" | "email"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const { loginEmail, ownerLogin } = useAuth();
  const navigate = useNavigate();

  function fmtMobile(v) {
    const d = String(v || "").replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return d.slice(0, 3) + " " + d.slice(3);
    return d.slice(0, 3) + " " + d.slice(3, 6) + " " + d.slice(6);
  }
  function digits(v, n) { return String(v || "").replace(/\D/g, "").slice(0, n); }

  async function submitEmail(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await loginEmail(email, password);
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  }

  async function submitMobile(e) {
    e.preventDefault();
    const m = digits(mobile, 10);
    if (m.length !== 10 || pin.length !== 6) return;
    setLoading(true);
    try {
      await ownerLogin({ mobile: "+63" + m, pin });
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid mobile number or PIN");
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-10 bg-secondary/40 border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <Link to="/" className="relative flex items-center gap-2.5">
          <Logo size={30} />
        </Link>
        <div className="relative">
          <blockquote className="font-display text-2xl leading-tight">
            "BizOne POS + Inventory replaced three tools in a weekend. Onboarding took 90 seconds."
          </blockquote>
          <div className="mt-4 text-sm text-muted-foreground">Maya · Founder, Elm & Oak Coffee Roasters</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl tracking-tighter">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in to your business.</p>

          {/* Tabs */}
          <div className="mt-6 inline-flex rounded-md border border-border p-0.5 bg-muted/50 text-xs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "mobile"}
              onClick={() => setTab("mobile")}
              data-testid="login-tab-mobile"
              className={`px-3 py-1.5 rounded-sm font-medium ${tab === "mobile" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
              Mobile + PIN
            </button>
            <button type="button" role="tab" aria-selected={tab === "email"}
              onClick={() => setTab("email")}
              data-testid="login-tab-email"
              className={`px-3 py-1.5 rounded-sm font-medium ${tab === "email" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
              Email
            </button>
          </div>

          {tab === "mobile" && (
            <form onSubmit={submitMobile} className="mt-5 space-y-4" data-testid="login-mobile-form">
              <div>
                <Label htmlFor="m">Mobile Number</Label>
                <div className="mt-1.5 flex items-stretch rounded-md border border-border focus-within:ring-2 focus-within:ring-ring overflow-hidden">
                  <div className="flex items-center gap-1 px-3 bg-muted text-sm font-medium"><Phone className="h-3.5 w-3.5" /> +63</div>
                  <Input id="m" required type="tel" inputMode="numeric" placeholder="9XX XXX XXXX"
                    data-testid="login-mobile"
                    value={fmtMobile(mobile)}
                    onChange={(e) => setMobile(digits(e.target.value, 10))}
                    className="border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0" />
                </div>
              </div>
              <div>
                <Label htmlFor="pin">6-Digit PIN</Label>
                <div className="mt-1.5 relative">
                  <Input id="pin" required type={showPin ? "text" : "password"} inputMode="numeric"
                    pattern="\d{6}" maxLength={6} placeholder="••••••"
                    data-testid="login-pin"
                    value={pin}
                    onChange={(e) => setPin(digits(e.target.value, 6))}
                    className="pr-10 tracking-[0.5em] font-mono" />
                  <button type="button" onClick={() => setShowPin((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
                    aria-label={showPin ? "Hide PIN" : "Show PIN"}>
                    {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Forgot PIN? <a href="/contact" className="underline">Contact BizOne Support</a>
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-mobile-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
              </Button>
            </form>
          )}

          {tab === "email" && (
            <>
              <button
                type="button"
                onClick={googleLogin}
                data-testid={TESTIDS.auth.googleBtn}
                className="mt-5 w-full h-10 rounded-md border border-border flex items-center justify-center gap-2 text-sm font-medium hover:bg-muted transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Continue with Google
              </button>

              <div className="mt-5 flex items-center gap-3 text-xs text-muted-foreground">
                <div className="h-px bg-border flex-1" /><span>or</span><div className="h-px bg-border flex-1" />
              </div>

              <form onSubmit={submitEmail} className="mt-5 space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required autoComplete="email"
                    data-testid={TESTIDS.auth.emailInput}
                    value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" required autoComplete="current-password"
                    data-testid={TESTIDS.auth.passwordInput}
                    value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid={TESTIDS.auth.submitBtn}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                </Button>
              </form>
            </>
          )}

          <p className="text-sm text-muted-foreground mt-5 text-center">
            New here?{" "}
            <Link to="/signup" data-testid={TESTIDS.auth.switchToSignup} className="text-foreground font-medium hover:underline">
              Create your business
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
